import styled, { css } from 'styled-components';
import {
  breakpoints,
  touchFeedback,
  touchTarget,
  textSize,
  ink,
  tones,
} from '../../../styles/brand';

const accent = '#ff9800';
const muted = '#6b6b6b';
const line = '#e8e8e8';

export const Page = styled.div`
  ${touchFeedback}

  max-width: 76rem;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
  font-family: 'RedHatDisplay-Regular';
  color: ${ink};
`;

/** Mesmo link de voltar da tela de detalhes do torneio. */
export const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 2.75rem;
  border: 0;
  background: none;
  padding: 0;
  font-family: 'RedHatDisplay-Bold';
  font-size: 1.1em;
  color: ${ink};
  opacity: 0.8;
  cursor: pointer;
  transition: opacity 0.3s;

  &:hover {
    opacity: 1;
  }

  &:active {
    opacity: 0.6;
  }
`;

export const Header = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.75rem 1.5rem;
  margin: 0.6rem 0 1.4rem;

  h1 {
    font-family: 'RedHatDisplay-Bold';
    font-size: clamp(1.5rem, 3vw, 2.1rem);
    line-height: 1.1;
    letter-spacing: -0.01em;
    margin: 0;
  }

  p {
    margin: 0.3rem 0 0;
    color: ${muted};
    font-size: ${textSize(0.9)};
  }
`;

export const Progress = styled.div`
  font-family: 'RedHatDisplay-Bold';
  font-variant-numeric: tabular-nums;
  font-size: ${textSize(0.95)};
  color: ${ink};

  span {
    color: ${muted};
    font-family: 'RedHatDisplay-Regular';
  }
`;

export const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(16rem, 22rem) minmax(0, 1fr);
  gap: 1.5rem;
  align-items: start;

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.8rem;

  button {
    border: 1px solid ${line};
    background: #fff;
    border-radius: 999px;
    min-height: 2.25rem;
    padding: 0.35rem 0.8rem;
    font-family: 'RedHatDisplay-Bold';
    font-size: ${textSize(0.8)};
    color: ${ink};
    cursor: pointer;
    touch-action: manipulation;
    transition:
      background 120ms ease-out,
      border-color 120ms ease-out;
  }

  button[aria-pressed='true'] {
    background: ${ink};
    border-color: ${ink};
    color: #fff;
  }

  button:active {
    transform: scale(0.97);
  }
`;

export const TeamList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid ${line};
  border-radius: 0.9rem;
  overflow: hidden;
  background: #fff;
`;

export const TeamItem = styled.li<{ $active: boolean }>`
  & + & {
    border-top: 1px solid ${line};
  }

  button {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.15rem 0.75rem;
    align-items: center;
    text-align: left;
    border: 0;
    min-height: ${touchTarget};
    padding: 0.75rem 0.9rem;
    touch-action: manipulation;
    background: ${({ $active }) => ($active ? '#fff7ec' : 'transparent')};
    box-shadow: ${({ $active }) =>
      $active ? `inset 3px 0 0 ${accent}` : 'none'};
    cursor: pointer;
    font: inherit;
    color: inherit;
  }

  button:active {
    background: #fff1dc;
  }

  strong {
    font-family: 'RedHatDisplay-Bold';
    font-size: ${textSize(0.95)};
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.2rem 0.5rem;
  }

  time {
    font-variant-numeric: tabular-nums;
    color: ${muted};
  }

  small {
    grid-column: 1;
    color: ${muted};
    font-size: ${textSize(0.78)};
  }
`;

export const Status = styled.span<{ $scored: boolean }>`
  grid-row: span 2;
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.75)};
  font-variant-numeric: tabular-nums;
  display: inline-flex;
  align-items: center;
  min-height: 1.9rem;
  padding: 0 0.65rem;
  border-radius: 999px;
  white-space: nowrap;
  ${({ $scored }) =>
    $scored
      ? css`
          color: ${tones.green.text};
          background: ${tones.green.soft};
        `
      : css`
          color: #8a6100;
          background: #fff4d6;
        `}
`;

/** "Agora" (equipe com a banca) e "A seguir" (próxima pendente da sala). */
export const Mark = styled.span<{ $mark: 'now' | 'next' }>`
  font-size: ${textSize(0.68)};
  letter-spacing: 0.02em;
  text-transform: uppercase;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  ${({ $mark }) =>
    $mark === 'now'
      ? css`
          color: #fff;
          background: ${accent};
        `
      : css`
          color: ${ink};
          background: #f0f0f0;
        `}
`;

export const GroupLabel = styled.h2`
  margin: 1.1rem 0 0.5rem;
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.75)};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${muted};
`;

export const Empty = styled.div`
  padding: 2.5rem 1rem;
  text-align: center;
  color: ${muted};
  border: 1px dashed ${line};
  border-radius: 1rem;
`;
